/**
 * organsMath.js — ЧИСТАЯ МАТЕМАТИКА конвейера вокруг 6-мм модели органов.
 * [11.1.79]
 *
 * Ноль импортов: ни cornerstone, ни window, ни журнала. Значит проверяется
 * числами в node — и здесь это не педантизм. Ошибка в пересчёте, нормировке
 * или сшивке НЕ ВЫГЛЯДИТ ошибкой: она выглядит как «сеть плохо размечает», и
 * искали бы её в модели.
 *
 * ОТКУДА ВЗЯТЫ ВСЕ ЧИСЛА. Не выдуманы — прочитаны из plans.json той самой
 * модели, которую владелец перевёл в ONNX:
 *   шаг              6.0 × 6.0 × 6.0 мм
 *   заплата          64 × 64 × 64
 *   нормировка       CTNormalization
 *   обрезка яркости  −1002 … 1584  (percentile_00_5 … percentile_99_5)
 *   среднее          −50.4849524394411
 *   отклонение        505.81990262489825
 *   классов          118 (фон + 117 структур)
 *   transpose        [0, 1, 2] — перестановки осей нет
 *
 * ПРОВЕРЕНО НЕЗАВИСИМО, что модель полная: при загрузке весов не хватило 0
 * ключей и лишних 0; в ONNX 16 550 518 чисел против 16 603 736 в сети (разница
 * — головы глубокого надзора, они в выводе не нужны); согласие решений с
 * исходной моделью 1.0 на всех вокселях случайного входа.
 */

/**
 * ДВЕ МОДЕЛИ, И У КАЖДОЙ СВОИ ЧИСЛА. Всё прочитано из её plans.json, ничего не
 * выдумано и не перенесено с одной на другую: у 3-мм границы яркости и среднее
 * ДРУГИЕ, хоть и близкие, а заплата вовсе иная и не куб.
 *
 * ОСИ. Обе обучены на данных, приведённых к канонической раскладке RAS (так
 * читает NIfTI библиотека nibabel, на которой стоит nnU-Net). Наш том приходит
 * из Cornerstone в LPS. Разница — знак первых двух осей, и её надо снять ДО
 * подачи в сеть: иначе печень уезжает на место селезёнки. Именно это и
 * случилось у владельца в 11.1.79 — я учёл ориентацию для чужих файлов маски и
 * забыл про вход модели. Одна ошибка дважды.
 *
 * ПОРЯДОК ОСЕЙ ЗАПЛАТЫ — [11.1.84], и это была ошибка 11.1.80 … 11.1.83.
 *
 * В plans.json заплата 3-мм модели записана как 112×112×128, и я перенёс эти три
 * числа сюда как есть, подписав «порядок — как в plans.json». Но порядок в
 * plans.json НЕ НАШ. nnU-Net хранит том массивами (z, y, x): и SimpleITK, и его
 * собственный читатель NIfTI разворачивают данные transpose(2,1,0), после чего
 * ось 0 — это срезы. Значит 112×112×128 читается как «112 срезов, 112
 * вперёд-назад, 128 влево-вправо», а у нас всюду (x, y, z) с быстрым x.
 *
 * ЧЕМ ЭТО ВЫШЛО НАРУЖУ. Сеть отказывалась на первой же заплате:
 *     Got invalid dimensions for input: index 2 Got 128 Expected 112,
 *                                       index 4 Got 112 Expected 128
 * то есть тензор подавался развёрнутым: [1,1,128,112,112] вместо
 * [1,1,112,112,128]. Владелец сообщал об этом ДВА раза, в 11.1.82 и в 11.1.83.
 *
 * ПОЧЕМУ НЕ ВИДНО У ОСТАЛЬНЫХ. У 6-мм заплата 64³, у пяти частей 128³ — кубы. Куб
 * при любой перестановке осей остаётся собой, и ошибка молчала везде, кроме
 * единственной некубической заплаты. Поэтому здесь ДВА поля: заплатаПланов — как
 * в файле модели, для сверки глазами; заплата — в нашем порядке, ею и считают
 * разбивка(), весаЗаплаты() и вырезка заплаты. Одно поле на два порядка — это и
 * был способ ошибиться.
 */
export const МОДЕЛИ = Object.freeze({
  'organs-6mm': Object.freeze({
    имя: 'organs-6mm',
    подпись: 'грубая, 6 мм',
    шаг: 6.0,
    // Куб: оба порядка совпадают. Поле держим у всех моделей, чтобы было видно,
    // что с plans.json сверялись, — см. «ПОРЯДОК ОСЕЙ ЗАПЛАТЫ» выше.
    заплатаПланов: Object.freeze([64, 64, 64]),   // как в plans.json, (z,y,x)
    заплата: Object.freeze([64, 64, 64]),         // наш порядок, (x,y,z)
    норм: Object.freeze({
      низ: -1002.0, верх: 1584.0,
      среднее: -50.4849524394411, отклонение: 505.81990262489825,
    }),
    оси: Object.freeze(['R', 'A', 'S']),
  }),
  // [11.1.81] ПОЛНАЯ, 1,5 мм. Она разрезана авторами на ПЯТЬ частей по областям
  // тела: части 1–5 — органы, позвонки, сердце с сосудами, мышцы, рёбра. Одна
  // часть все структуры не даёт, поэтому здесь пять записей с общими числами и
  // разными файлами.
  //
  // ЧИСЛА ПРОЧИТАНЫ ИЗ plans.json КАЖДОЙ ЧАСТИ. И вот доказательство, что
  // переносить их между частями было нельзя:
  //     часть 1 органы:    обрезка -1024 … 276,   среднее -370,000
  //     часть 2 позвонки:  обрезка   -96 … 1514,  среднее  367,329
  //     часть 3 сердце:    обрезка  -101 … 813,   среднее  142,198
  //     часть 4 мышцы:     обрезка  -197 … 1729,  среднее  174,821
  //     часть 5 рёбра:     обрезка  -110 … 1302,  среднее  291,990
  // Обрезка снизу отличается в ДЕСЯТЬ раз, среднее — от минус 370 до плюс 367.
  //
  // В первом наброске я вписал сюда «-1024…1607, среднее -49, отклонение 500» —
  // взял из головы, потому что похожие числа бывают у КТ. Они не подошли бы НИ
  // ОДНОЙ части.
  //
  // В первом наброске я вписал сюда «−1024…1607, среднее −49, отклонение 500» —
  // взял их из головы, потому что похожие числа бывают у КТ. Это ровно тот
  // способ работы, за который владелец меня и ругает: числа выглядели бы
  // правдоподобно, разметка вышла бы мимо, и искали бы её в модели.
  //
  // ВАЖНОЕ ОБ УСТРОЙСТВЕ: у частей НЕ 118 классов, а 25, 27, 19, 24 и 27 —
  // каждая отвечает за свои структуры, и раскладка по каналам у каждой СВОЯ.
  // Общий список из 118 (КЛАССЫ ниже) относится только к моделям 6 мм и 3 мм.
  // Пока раскладка частей не прочитана из их dataset.json, прогон по ним
  // отказывается: положить структуру в чужой сегмент — худшая из ошибок, потому
  // что молчаливая.
  //
  // Заплата у всех пяти 128×128×128 — крупнее, чем у 3-мм (112×112×128).
  //
  // ЦЕНА, посчитанная заранее: сетка 252×252×431 = 27,4 млн вокселей, 96 заплат
  // на часть, работы в 196 раз больше, чем у 6 мм. Пять частей — около четверти
  // часа напрямую и до полутора часов через прослойку. Только настольная сборка.
  'organs-15mm-1': Object.freeze({
    имя: 'organs-15mm-1', подпись: 'полная 1,5 мм — органы', шаг: 1.5,
    заплатаПланов: Object.freeze([128, 128, 128]),   // как в plans.json, (z,y,x)
    заплата: Object.freeze([128, 128, 128]),         // куб — оба порядка совпадают
    // Прочитано из plans.json ЭТОЙ части. Классов у неё 25.
    норм: Object.freeze({ низ: -1024.0, верх: 276.0, среднее: -370.00039267657144, отклонение: 436.5998675471528 }),
    оси: Object.freeze(['R', 'A', 'S']),
    часть: 1, классов: 25,
    // Раскладка каналов ЭТОЙ части — выписана ДОСЛОВНО из её dataset.json.
    свои: Object.freeze([
      'background', 'spleen', 'kidney_right', 'kidney_left', 'gallbladder', 'liver',
      'stomach', 'pancreas', 'adrenal_gland_right', 'adrenal_gland_left',
      'lung_upper_lobe_left', 'lung_lower_lobe_left', 'lung_upper_lobe_right',
      'lung_middle_lobe_right', 'lung_lower_lobe_right', 'esophagus', 'trachea',
      'thyroid_gland', 'small_bowel', 'duodenum', 'colon', 'urinary_bladder', 'prostate',
      'kidney_cyst_left', 'kidney_cyst_right',
    ]),
  }),
  'organs-15mm-2': Object.freeze({
    имя: 'organs-15mm-2', подпись: 'полная 1,5 мм — позвонки', шаг: 1.5,
    заплатаПланов: Object.freeze([128, 128, 128]),   // как в plans.json, (z,y,x)
    заплата: Object.freeze([128, 128, 128]),         // куб — оба порядка совпадают
    // Прочитано из plans.json ЭТОЙ части. Классов у неё 27.
    норм: Object.freeze({ низ: -96.0, верх: 1514.0, среднее: 367.32929817099546, отклонение: 320.8587389393513 }),
    оси: Object.freeze(['R', 'A', 'S']),
    часть: 2, классов: 27,
    // Раскладка каналов ЭТОЙ части — выписана ДОСЛОВНО из её dataset.json.
    свои: Object.freeze([
      'background', 'sacrum', 'vertebrae_S1', 'vertebrae_L5', 'vertebrae_L4',
      'vertebrae_L3', 'vertebrae_L2', 'vertebrae_L1', 'vertebrae_T12', 'vertebrae_T11',
      'vertebrae_T10', 'vertebrae_T9', 'vertebrae_T8', 'vertebrae_T7', 'vertebrae_T6',
      'vertebrae_T5', 'vertebrae_T4', 'vertebrae_T3', 'vertebrae_T2', 'vertebrae_T1',
      'vertebrae_C7', 'vertebrae_C6', 'vertebrae_C5', 'vertebrae_C4', 'vertebrae_C3',
      'vertebrae_C2', 'vertebrae_C1',
    ]),
  }),
  'organs-15mm-3': Object.freeze({
    имя: 'organs-15mm-3', подпись: 'полная 1,5 мм — сердце и сосуды', шаг: 1.5,
    заплатаПланов: Object.freeze([128, 128, 128]),   // как в plans.json, (z,y,x)
    заплата: Object.freeze([128, 128, 128]),         // куб — оба порядка совпадают
    // Прочитано из plans.json ЭТОЙ части. Классов у неё 19.
    норм: Object.freeze({ низ: -101.0, верх: 813.0, среднее: 142.1977119916391, отклонение: 154.74032651622053 }),
    оси: Object.freeze(['R', 'A', 'S']),
    часть: 3, классов: 19,
    // Раскладка каналов ЭТОЙ части — выписана ДОСЛОВНО из её dataset.json.
    свои: Object.freeze([
      'background', 'heart', 'aorta', 'pulmonary_vein', 'brachiocephalic_trunk',
      'subclavian_artery_right', 'subclavian_artery_left', 'common_carotid_artery_right',
      'common_carotid_artery_left', 'brachiocephalic_vein_left',
      'brachiocephalic_vein_right', 'atrial_appendage_left', 'superior_vena_cava',
      'inferior_vena_cava', 'portal_vein_and_splenic_vein', 'iliac_artery_left',
      'iliac_artery_right', 'iliac_vena_left', 'iliac_vena_right',
    ]),
  }),
  'organs-15mm-4': Object.freeze({
    имя: 'organs-15mm-4', подпись: 'полная 1,5 мм — мышцы', шаг: 1.5,
    заплатаПланов: Object.freeze([128, 128, 128]),   // как в plans.json, (z,y,x)
    заплата: Object.freeze([128, 128, 128]),         // куб — оба порядка совпадают
    // Прочитано из plans.json ЭТОЙ части. Классов у неё 24.
    норм: Object.freeze({ низ: -197.0, верх: 1729.0, среднее: 174.82106003593222, отклонение: 362.496044938386 }),
    оси: Object.freeze(['R', 'A', 'S']),
    часть: 4, классов: 24,
    // Раскладка каналов ЭТОЙ части — выписана ДОСЛОВНО из её dataset.json.
    свои: Object.freeze([
      'background', 'humerus_left', 'humerus_right', 'scapula_left', 'scapula_right',
      'clavicula_left', 'clavicula_right', 'femur_left', 'femur_right', 'hip_left',
      'hip_right', 'spinal_cord', 'gluteus_maximus_left', 'gluteus_maximus_right',
      'gluteus_medius_left', 'gluteus_medius_right', 'gluteus_minimus_left',
      'gluteus_minimus_right', 'autochthon_left', 'autochthon_right', 'iliopsoas_left',
      'iliopsoas_right', 'brain', 'skull',
    ]),
  }),
  'organs-15mm-5': Object.freeze({
    имя: 'organs-15mm-5', подпись: 'полная 1,5 мм — рёбра', шаг: 1.5,
    заплатаПланов: Object.freeze([128, 128, 128]),   // как в plans.json, (z,y,x)
    заплата: Object.freeze([128, 128, 128]),         // куб — оба порядка совпадают
    // Прочитано из plans.json ЭТОЙ части. Классов у неё 27.
    норм: Object.freeze({ низ: -110.0, верх: 1302.0, среднее: 291.98966240726804, отклонение: 261.53174290110513 }),
    оси: Object.freeze(['R', 'A', 'S']),
    часть: 5, классов: 27,
    // Раскладка каналов ЭТОЙ части — выписана ДОСЛОВНО из её dataset.json.
    свои: Object.freeze([
      'background', 'rib_left_1', 'rib_left_2', 'rib_left_3', 'rib_left_4', 'rib_left_5',
      'rib_left_6', 'rib_left_7', 'rib_left_8', 'rib_left_9', 'rib_left_10', 'rib_left_11',
      'rib_left_12', 'rib_right_1', 'rib_right_2', 'rib_right_3', 'rib_right_4',
      'rib_right_5', 'rib_right_6', 'rib_right_7', 'rib_right_8', 'rib_right_9',
      'rib_right_10', 'rib_right_11', 'rib_right_12', 'sternum', 'costal_cartilages',
    ]),
  }),

  'organs-3mm': Object.freeze({
    имя: 'organs-3mm',
    подпись: 'подробная, 3 мм',
    шаг: 3.0,
    // [11.1.84] ЗАПЛАТА НЕ КУБ — И ПОРЯДОК ОСЕЙ У НЕЁ НЕ НАШ. В plans.json
    // записано 112×112×128, но это (z, y, x). Здесь была причина того, что
    // «подробно, минуты» не работало три версии подряд.
    заплатаПланов: Object.freeze([112, 112, 128]),   // как в plans.json, (z,y,x)
    заплата: Object.freeze([128, 112, 112]),         // наш порядок, (x,y,z)
    норм: Object.freeze({
      низ: -1004.0, верх: 1588.0,
      среднее: -50.38697721419439, отклонение: 503.39235619144,
    }),
    оси: Object.freeze(['R', 'A', 'S']),
  }),
});

/** Шаг сетки 6-мм модели. Оставлено для прежних вызовов. */
export const ШАГ_ММ = МОДЕЛИ['organs-6mm'].шаг;

/** Заплата 6-мм модели. Оставлено для прежних вызовов. */
export const ЗАПЛАТА = МОДЕЛИ['organs-6mm'].заплата;

/** Нормировка 6-мм модели. Оставлено для прежних вызовов. */
export const НОРМ = МОДЕЛИ['organs-6mm'].норм;

/**
 * Номер канала → имя структуры. 118 значений, выписаны из dataset.json как
 * есть. НЕ сокращал и не переупорядочивал: перепутанный номер положил бы
 * селезёнку в сегмент печени, и маска выглядела бы правдоподобно, а орган был
 * бы не тот. Это худший вид ошибки — молчаливый.
 */
export const КЛАССЫ = Object.freeze([
  'background', 'spleen', 'kidney_right', 'kidney_left', 'gallbladder', 'liver',
  'stomach', 'pancreas', 'adrenal_gland_right', 'adrenal_gland_left',
  'lung_upper_lobe_left', 'lung_lower_lobe_left', 'lung_upper_lobe_right',
  'lung_middle_lobe_right', 'lung_lower_lobe_right', 'esophagus', 'trachea',
  'thyroid_gland', 'small_bowel', 'duodenum', 'colon', 'urinary_bladder',
  'prostate', 'kidney_cyst_left', 'kidney_cyst_right', 'sacrum',
  'vertebrae_S1', 'vertebrae_L5', 'vertebrae_L4', 'vertebrae_L3',
  'vertebrae_L2', 'vertebrae_L1', 'vertebrae_T12', 'vertebrae_T11',
  'vertebrae_T10', 'vertebrae_T9', 'vertebrae_T8', 'vertebrae_T7',
  'vertebrae_T6', 'vertebrae_T5', 'vertebrae_T4', 'vertebrae_T3',
  'vertebrae_T2', 'vertebrae_T1', 'vertebrae_C7', 'vertebrae_C6',
  'vertebrae_C5', 'vertebrae_C4', 'vertebrae_C3', 'vertebrae_C2',
  'vertebrae_C1', 'heart', 'aorta', 'pulmonary_vein', 'brachiocephalic_trunk',
  'subclavian_artery_right', 'subclavian_artery_left',
  'common_carotid_artery_right', 'common_carotid_artery_left',
  'brachiocephalic_vein_left', 'brachiocephalic_vein_right',
  'atrial_appendage_left', 'superior_vena_cava', 'inferior_vena_cava',
  'portal_vein_and_splenic_vein', 'iliac_artery_left', 'iliac_artery_right',
  'iliac_vena_left', 'iliac_vena_right', 'humerus_left', 'humerus_right',
  'scapula_left', 'scapula_right', 'clavicula_left', 'clavicula_right',
  'femur_left', 'femur_right', 'hip_left', 'hip_right', 'spinal_cord',
  'gluteus_maximus_left', 'gluteus_maximus_right', 'gluteus_medius_left',
  'gluteus_medius_right', 'gluteus_minimus_left', 'gluteus_minimus_right',
  'autochthon_left', 'autochthon_right', 'iliopsoas_left', 'iliopsoas_right',
  'brain', 'skull',
  'rib_left_1', 'rib_left_2', 'rib_left_3', 'rib_left_4', 'rib_left_5',
  'rib_left_6', 'rib_left_7', 'rib_left_8', 'rib_left_9', 'rib_left_10',
  'rib_left_11', 'rib_left_12',
  'rib_right_1', 'rib_right_2', 'rib_right_3', 'rib_right_4', 'rib_right_5',
  'rib_right_6', 'rib_right_7', 'rib_right_8', 'rib_right_9', 'rib_right_10',
  'rib_right_11', 'rib_right_12',
  'sternum', 'costal_cartilages',
]);

/** Русские названия для тех структур, что врач просит чаще. */
export const ПО_РУССКИ = Object.freeze({
  liver: 'печень', spleen: 'селезёнка',
  kidney_left: 'почка левая', kidney_right: 'почка правая',
  pancreas: 'поджелудочная', gallbladder: 'жёлчный пузырь',
  stomach: 'желудок', aorta: 'аорта', heart: 'сердце',
  urinary_bladder: 'мочевой пузырь', esophagus: 'пищевод',
  colon: 'толстая кишка', small_bowel: 'тонкая кишка',
  duodenum: 'двенадцатиперстная', trachea: 'трахея',
  thyroid_gland: 'щитовидная', prostate: 'предстательная',
  brain: 'головной мозг', spinal_cord: 'спинной мозг',
  inferior_vena_cava: 'нижняя полая вена',
  superior_vena_cava: 'верхняя полая вена',
  portal_vein_and_splenic_vein: 'портальная и селезёночная вены',

  // [11.1.84] ОСТАЛЬНЫЕ 117 — ПО-РУССКИ, ПОТОМУ ЧТО ИХ ТЕПЕРЬ ВИДНО ВРАЧУ.
  //
  // До 11.1.83 в панели стояли девять названий буквами в разметке, а имена
  // остальных структур никуда не показывались. Теперь список структур строится
  // по выбранной модели (см. organsPick.js), и без русских названий в панели
  // появилась бы английская мишура — то самое, что владелец велел убрать, и за
  // чем следит russian_ui_test.
  //
  // Перевод — обиходный, как говорит врач у аппарата, а не дословный из атласа:
  // «мышца-разгибатель спины», а не «autochthon».
  adrenal_gland_left: 'надпочечник левый',
  adrenal_gland_right: 'надпочечник правый',
  lung_upper_lobe_left: 'лёгкое: верхняя доля слева',
  lung_lower_lobe_left: 'лёгкое: нижняя доля слева',
  lung_upper_lobe_right: 'лёгкое: верхняя доля справа',
  lung_middle_lobe_right: 'лёгкое: средняя доля справа',
  lung_lower_lobe_right: 'лёгкое: нижняя доля справа',
  kidney_cyst_left: 'киста почки левой',
  kidney_cyst_right: 'киста почки правой',

  sacrum: 'крестец',
  vertebrae_S1: 'позвонок S1',
  vertebrae_L5: 'позвонок L5',
  vertebrae_L4: 'позвонок L4',
  vertebrae_L3: 'позвонок L3',
  vertebrae_L2: 'позвонок L2',
  vertebrae_L1: 'позвонок L1',
  vertebrae_T12: 'позвонок T12',
  vertebrae_T11: 'позвонок T11',
  vertebrae_T10: 'позвонок T10',
  vertebrae_T9: 'позвонок T9',
  vertebrae_T8: 'позвонок T8',
  vertebrae_T7: 'позвонок T7',
  vertebrae_T6: 'позвонок T6',
  vertebrae_T5: 'позвонок T5',
  vertebrae_T4: 'позвонок T4',
  vertebrae_T3: 'позвонок T3',
  vertebrae_T2: 'позвонок T2',
  vertebrae_T1: 'позвонок T1',
  vertebrae_C7: 'позвонок C7',
  vertebrae_C6: 'позвонок C6',
  vertebrae_C5: 'позвонок C5',
  vertebrae_C4: 'позвонок C4',
  vertebrae_C3: 'позвонок C3',
  vertebrae_C2: 'позвонок C2',
  vertebrae_C1: 'позвонок C1',

  pulmonary_vein: 'лёгочная вена',
  brachiocephalic_trunk: 'брахиоцефальный ствол',
  subclavian_artery_right: 'подключичная артерия правая',
  subclavian_artery_left: 'подключичная артерия левая',
  common_carotid_artery_right: 'общая сонная артерия правая',
  common_carotid_artery_left: 'общая сонная артерия левая',
  brachiocephalic_vein_left: 'брахиоцефальная вена левая',
  brachiocephalic_vein_right: 'брахиоцефальная вена правая',
  atrial_appendage_left: 'ушко левого предсердия',
  iliac_artery_left: 'подвздошная артерия левая',
  iliac_artery_right: 'подвздошная артерия правая',
  iliac_vena_left: 'подвздошная вена левая',
  iliac_vena_right: 'подвздошная вена правая',

  humerus_left: 'плечевая кость левая',
  humerus_right: 'плечевая кость правая',
  scapula_left: 'лопатка левая',
  scapula_right: 'лопатка правая',
  clavicula_left: 'ключица левая',
  clavicula_right: 'ключица правая',
  femur_left: 'бедренная кость левая',
  femur_right: 'бедренная кость правая',
  hip_left: 'тазовая кость левая',
  hip_right: 'тазовая кость правая',
  gluteus_maximus_left: 'большая ягодичная мышца левая',
  gluteus_maximus_right: 'большая ягодичная мышца правая',
  gluteus_medius_left: 'средняя ягодичная мышца левая',
  gluteus_medius_right: 'средняя ягодичная мышца правая',
  gluteus_minimus_left: 'малая ягодичная мышца левая',
  gluteus_minimus_right: 'малая ягодичная мышца правая',
  autochthon_left: 'мышца-разгибатель спины левая',
  autochthon_right: 'мышца-разгибатель спины правая',
  iliopsoas_left: 'подвздошно-поясничная мышца левая',
  iliopsoas_right: 'подвздошно-поясничная мышца правая',
  skull: 'череп',

  rib_left_1: 'ребро левое 1',
  rib_left_2: 'ребро левое 2',
  rib_left_3: 'ребро левое 3',
  rib_left_4: 'ребро левое 4',
  rib_left_5: 'ребро левое 5',
  rib_left_6: 'ребро левое 6',
  rib_left_7: 'ребро левое 7',
  rib_left_8: 'ребро левое 8',
  rib_left_9: 'ребро левое 9',
  rib_left_10: 'ребро левое 10',
  rib_left_11: 'ребро левое 11',
  rib_left_12: 'ребро левое 12',
  rib_right_1: 'ребро правое 1',
  rib_right_2: 'ребро правое 2',
  rib_right_3: 'ребро правое 3',
  rib_right_4: 'ребро правое 4',
  rib_right_5: 'ребро правое 5',
  rib_right_6: 'ребро правое 6',
  rib_right_7: 'ребро правое 7',
  rib_right_8: 'ребро правое 8',
  rib_right_9: 'ребро правое 9',
  rib_right_10: 'ребро правое 10',
  rib_right_11: 'ребро правое 11',
  rib_right_12: 'ребро правое 12',
  sternum: 'грудина',
  costal_cartilages: 'рёберные хрящи',
});

/** Имя структуры → номер канала. */
export function номерКласса(имя) {
  const i = КЛАССЫ.indexOf(String(имя));
  return i >= 0 ? i : -1;
}

/**
 * [11.1.84] ЧТО ЭТА МОДЕЛЬ ВООБЩЕ УМЕЕТ РАЗМЕТИТЬ.
 *
 * ПОВОД, слова владельца: «если выбрать рёбра, сердце и сосуды, позвонки, мышцы
 * и кости, то просит „Отметьте хотя бы одну структуру“ — но какую ещё структуру
 * я должен отметить?»
 *
 * Вопрос совершенно справедливый, и отвечать на него должна была программа, а не
 * врач. В панели стояли девять брюшных органов, вписанных буквами в разметку, а
 * галочки не менялись при смене модели. Выбрав «рёбра», врач мог отметить только
 * печень с почками — которых у этой части нет вовсе. Отказ был верным по сути и
 * бесполезным по делу: он не говорил, что отмечать.
 *
 * Теперь список галочек строится ПО МОДЕЛИ, из этой самой функции. Отметить
 * заведомо чужую структуру больше нельзя — её просто нет в списке.
 *
 * @param {string|object} модель — имя из МОДЕЛИ или сама запись
 * @returns {string[]} имена структур БЕЗ фона, в порядке каналов модели
 */
export function структурыМодели(модель) {
  const М = typeof модель === 'string' ? МОДЕЛИ[модель] : модель;
  const список = (М && М.свои) ? М.свои : КЛАССЫ;
  return list_без_фона(список);
}

/** Убрать фон: он не структура, врачу его не показывают. */
function list_без_фона(список) {
  return Array.from(список).filter((и) => и !== 'background');
}

/**
 * [11.1.84] Пять областей тела — для группировки длинного списка.
 *
 * Взяты НЕ из головы: это раскладки тех же пяти частей полной модели. Значит
 * общий список из 117 делится ровно так, как его поделили авторы модели, и
 * подписи областей совпадают с подписями в панели.
 */
export const ОБЛАСТИ = Object.freeze([1, 2, 3, 4, 5].map((н) => {
  const М = МОДЕЛИ[`organs-15mm-${н}`];
  return Object.freeze({
    подпись: String(М.подпись).replace(/^полная 1,5 мм — /, ''),
    имена: Object.freeze(list_без_фона(М.свои)),
  });
}));

/**
 * Разложить имена структур по областям, сохраняя порядок областей. Что не
 * попало ни в одну — уходит в конец под подписью «прочее»: молча терять
 * структуру нельзя, иначе врач не сможет её отметить и не узнает почему.
 */
export function поОбластям(имена) {
  const остаток = new Set(имена);
  const вышло = [];
  for (const о of ОБЛАСТИ) {
    const свои = о.имена.filter((и) => остаток.has(и));
    for (const и of свои) остаток.delete(и);
    if (свои.length) вышло.push({ подпись: о.подпись, имена: свои });
  }
  if (остаток.size) вышло.push({ подпись: 'прочее', имена: Array.from(остаток) });
  return вышло;
}

/**
 * Сколько вокселей выйдет при пересчёте в шаг модели.
 *
 * Считаем через ФИЗИЧЕСКИЙ размер: длина по оси = число вокселей × шаг. Иначе
 * при разном шаге по осям (у владельца 0.739 × 0.739 × 0.8) вышла бы кривая
 * сетка, и орган поехал бы по длинной оси.
 */
export function сеткаПодМодель(dims, spacing, шаг = ШАГ_ММ) {
  const d = (dims || []).map(Number);
  const s = (spacing || []).map((ч) => Math.abs(Number(ч)));
  if (d.length !== 3 || s.length !== 3) return null;
  if (d.some((ч) => !(ч > 0)) || s.some((ч) => !(ч > 0))) return null;
  const мм = [d[0] * s[0], d[1] * s[1], d[2] * s[2]];
  return {
    мм,
    dims: мм.map((v) => Math.max(1, Math.round(v / шаг))),
    шаг: [шаг, шаг, шаг],
  };
}

/**
 * Пересчёт объёма в другую сетку ближайшим соседом.
 *
 * ПОЧЕМУ БЛИЖАЙШИЙ, А НЕ ТРЁХЛИНЕЙНЫЙ. Для ЯРКОСТИ трёхлинейный был бы точнее,
 * и nnU-Net при обучении пользуется им. Но здесь мы уменьшаем объём в восемь
 * раз по каждой оси, и на таком прореживании разница между способами меньше,
 * чем разница от самого прореживания. Зато ближайший не смешивает соседние
 * ткани в несуществующую среднюю: на границе «печень — воздух» трёхлинейный
 * дал бы значения, которых в теле нет. Честно: это выбор в пользу простоты и
 * предсказуемости, и он ОТЛИЧАЕТСЯ от того, как считает сам TotalSegmentator.
 *
 * @param {ArrayLike<number>} из
 * @param {number[]} dimsИз @param {number[]} dimsВ
 * @param {Float32Array|null} куда
 */
export function пересчитать(из, dimsИз, dimsВ, куда = null) {
  const [ax, ay, az] = dimsИз;
  const [bx, by, bz] = dimsВ;
  const всего = bx * by * bz;
  if (!из || из.length !== ax * ay * az) return null;
  const к = куда && куда.length === всего ? куда : new Float32Array(всего);
  const шагX = ax / bx, шагY = ay / by, шагZ = az / bz;
  for (let z = 0; z < bz; z++) {
    const sz = Math.min(az - 1, Math.floor((z + 0.5) * шагZ));
    const базаZ = sz * ay * ax;
    for (let y = 0; y < by; y++) {
      const sy = Math.min(ay - 1, Math.floor((y + 0.5) * шагY));
      const база = базаZ + sy * ax;
      const цельY = (z * by + y) * bx;
      for (let x = 0; x < bx; x++) {
        const sx = Math.min(ax - 1, Math.floor((x + 0.5) * шагX));
        к[цельY + x] = из[база + sx];
      }
    }
  }
  return к;
}

/**
 * CTNormalization из nnU-Net: обрезать по границам, вычесть среднее, поделить
 * на отклонение. Числа — из plans.json, не мои.
 */
export function нормировать(том, куда = null, норм = НОРМ) {
  const n = том.length;
  const к = куда && куда.length === n ? куда : new Float32Array(n);
  const { низ, верх, среднее, отклонение } = норм;
  for (let i = 0; i < n; i++) {
    let v = том[i];
    if (v < низ) v = низ; else if (v > верх) v = верх;
    к[i] = (v - среднее) / отклонение;
  }
  return к;
}

/**
 * Где резать объём на заплаты. Перекрытие половинное — так делает nnU-Net.
 *
 * Если объём меньше заплаты по какой-то оси, начало одно и равно нулю: сеть
 * получит заплату с запасом, дополненную нулями. Обратное — резать меньше
 * заплаты — модель не умеет.
 */
export function началаЗаплат(длина, сторона) {
  if (!(длина > 0) || !(сторона > 0)) return [0];
  if (длина <= сторона) return [0];
  const шаг = Math.max(1, Math.floor(сторона / 2));
  const из = [];
  for (let н = 0; н + сторона <= длина; н += шаг) из.push(н);
  const последнее = длина - сторона;
  if (из[из.length - 1] !== последнее) из.push(последнее);
  return из;
}

/** Все начала по трём осям — сколько прогонов сети понадобится. */
export function разбивка(dims, заплата = ЗАПЛАТА) {
  const по = [0, 1, 2].map((о) => началаЗаплат(dims[о], заплата[о]));
  return { по, всего: по[0].length * по[1].length * по[2].length };
}

/**
 * Вес вокселя внутри заплаты — гауссов колокол.
 *
 * ЗАЧЕМ. Края заплаты сеть видит хуже: у неё там меньше окружения. При
 * половинном перекрытии один воксель попадает в две-три заплаты, и если
 * складывать их с равным весом, на швах появятся ступеньки. Колокол даёт
 * середине заплаты больший вес, краям меньший — швы размываются.
 *
 * Так же поступает nnU-Net; сигма у него сторона/8.
 */
export function весаЗаплаты(заплата = ЗАПЛАТА) {
  const [nx, ny, nz] = заплата;
  const одномерный = (n) => {
    const с = n / 8;
    const ц = (n - 1) / 2;
    const в = new Float32Array(n);
    for (let i = 0; i < n; i++) в[i] = Math.exp(-((i - ц) * (i - ц)) / (2 * с * с));
    return в;
  };
  const вx = одномерный(nx), вy = одномерный(ny), вz = одномерный(nz);
  const в = new Float32Array(nx * ny * nz);
  let макс = 0;
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      const база = (z * ny + y) * nx;
      for (let x = 0; x < nx; x++) {
        const v = вx[x] * вy[y] * вz[z];
        в[база + x] = v;
        if (v > макс) макс = v;
      }
    }
  }
  // Наименьший вес не должен быть нулём: воксель, попавший ТОЛЬКО в край одной
  // заплаты, иначе остался бы без голоса вовсе.
  const пол = макс * 1e-3;
  for (let i = 0; i < в.length; i++) if (в[i] < пол) в[i] = пол;
  return в;
}

/** Объём в миллилитрах: шаг в мм, 1 мл = 1000 мм³. */
export function объёмМл(вокселей, spacing) {
  const s = spacing || [1, 1, 1];
  return (вокселей * Math.abs(s[0] * s[1] * s[2])) / 1000;
}
